<template>
  <div class="file-drag-drop">
    <form ref="fileform" :class="{ 'is-dropping': isDropping === true }">
      <div class="drop-instructions">
        <span class="drop-icon">
          <font-awesome-icon icon="file-upload" />
        </span>
        <span v-if="dragAndDropCapable">
          <div class="drop-label">{{ $t('attachments.labels.drop') }}</div>
          <div class="select-label" @click="onSelectFile">
            {{ $t('attachments.labels.select') }}
          </div>
        </span>
        <span v-else>
          <div class="select-label" @click="onSelectFile">
            {{ $t('attachments.labels.selectOnly') }}
          </div>
        </span>
      </div>
      <div class="allowed-files">({{ $t('attachments.labels.allowedFiles') }})</div>
      <input ref="fileInput" class="file-input" type="file" @change="handleFileInput" />
    </form>
  </div>
</template>

<script>
import { attachmentService } from './attachment.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';

// Borrowed from https://serversideup.net/drag-and-drop-file-uploads-with-vuejs-and-axios/
// Please see the above link from displaying a loading bar and other details
export default {
  name: 'DragNDropPad',
  props: {
    referenceNumber: {
      type: String,
      required: true
    },
    owner: {
      type: String,
      required: true
    },
    category: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dragAndDropCapable: false,
      files: [],
      isDropping: false
    };
  },
  mounted() {
    this.dragAndDropCapable = this.determineDragAndDropCapable();
    if (this.dragAndDropCapable) {
      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(
        event => {
          this.$refs.fileform.addEventListener(
            event,
            function(e) {
              e.preventDefault();
              e.stopPropagation();
            }.bind(this),
            false
          );
        }
      );

      this.$refs.fileform.addEventListener('drag', () => {
        this.isDropping = true;
      });
      this.$refs.fileform.addEventListener('dragstart', () => {
        this.isDropping = true;
      });
      this.$refs.fileform.addEventListener('dragenter', () => {
        this.isDropping = true;
      });
      this.$refs.fileform.addEventListener('dragover', () => {
        this.isDropping = true;
      });

      this.$refs.fileform.addEventListener('dragleave', () => {
        this.isDropping = false;
      });

      this.$refs.fileform.addEventListener('drop', event => {
        // Capture the local file - we are only supporting one for now.
        if (event.dataTransfer.files.length > 0) {
          this.files.push(event.dataTransfer.files[0]);
        }
        this.submitFile();
        this.isDropping = false;
      });
    }
  },

  methods: {
    clearFiles() {
      this.files = [];
    },
    determineDragAndDropCapable() {
      let div = document.createElement('div');
      return (
        ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) &&
        'FormData' in window &&
        'FileReader' in window
      );
    },
    onSelectFile() {
      this.$refs.fileInput.click();
    },
    handleFileInput() {
      this.files.push(this.$refs.fileInput.files[0]);
      this.submitFile();
    },
    submitFile() {
      if (!this.files.length) {
        return;
      }
      let formData = new FormData();
      formData.append('file', this.files[0]);
      formData.append('owner', this.owner);
      formData.append('category', this.category);
      attachmentService
        .doAttachment(this.referenceNumber, formData)
        .then(() => {
          this.clearFiles();
          this.$emit('attachment-success');
        })
        .catch(err => {
          const errMessage =
            err.status && err.status === 405
              ? this.$t('attachments.errors.unsupported')
              : this.$t('attachments.errors.uploaFailed');
          this.$store.dispatch(TolErrorsActions.SetError, {
            message: errMessage,
            items: this.files.map(f => f.name)
          });
          this.clearFiles();
          this.$emit('attachment-failure');
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../styles/colors.scss';
@import '../../styles/variables.scss';

.file-drag-drop {
  form {
    display: flex;
    justify-content: center;
    padding: 40px;
    background-color: $accent-blue;
    text-align: center;
    font-size: 115%;
    border: 1px dashed $primary-blue-2;
    &.is-dropping {
      background-color: $light-blue;
    }
  }
  .drop-file {
    color: black;
    font-weight: bold;
  }
  .drop-icon {
    color: $primary-blue-2;
    display: inline-block;
  }
  .drop-instructions {
    .drop-label {
      display: inline-block;
      margin-left: 5px;
    }
    .select-label {
      display: inline-block;
      color: $primary-blue-2;
      font-weight: bold;
      cursor: pointer;
      margin-left: 5px;
    }
  }
  .file-input {
    display: block;
    visibility: hidden;
    width: 0;
    height: 0;
  }
  .allowed-files {
    color: $light-grey-3;
    margin-left: 12px;
  }
}
</style>
